import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { TextField, IconButton, Button, Autocomplete, Box, Typography } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

// Shared enterprise style object matching production form configurations
const enterpriseKitInputStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid #E2E8F0',
    '& fieldset': { border: 'none' },
    '&:hover': {
      borderColor: '#CBD5E1',
      boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
    },
    '&.Mui-focused': {
      borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT MATRIX ---
      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
    }
  },
  '& .MuiInputLabel-outlined': {
    color: '#64748B',
    fontWeight: 500,
    transform: 'translate(14px, 10px) scale(1)',
    '&.Mui-focused, &.MuiInputLabel-shrink': {
      transform: 'translate(14px, -8px) scale(0.75)',
      color: '#1976d2',
      fontWeight: 600
    }
  },
  '& .MuiSelect-select, & input': {
    padding: '10px 14px !important',
    fontSize: '13.5px',
    color: '#1E293B',
    fontWeight: 500
  },
  '& .MuiFormHelperText-root': {
    mx: 0.5,
    mt: 0.75,
    fontWeight: 500
  }
};
const enterpriseModalInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT MATRIX ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiFilledInput-root': {
        borderRadius: '10px',
        backgroundColor: '#F1F5F9',
        border: '1px solid #E2E8F0',
        '&::before, &::after': { display: 'none' },
        '&:hover': { backgroundColor: '#E2E8F0' }
    },
    '& .MuiInputLabel-root': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& .MuiSelect-select, & input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};
const KitDetailsForm = forwardRef(({ modalTitle, isKit, initialRows = [], skuOnly = [] }, ref) => {
  const [rows, setRows] = useState([
    { id: '', itemSku: '', skuquantity: 0, itemSkuError: '', quantityError: '' },
  ]);

  const isEditMode = modalTitle === 'Items Edit' || modalTitle === 'New Items Edit';

  // Sync with parent-provided initialRows
  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      const mapped = initialRows.map(item => ({
        id: item.id,
        itemSku: item.item_sku || '',
        skuquantity: item.quantity || '',
        itemSkuError: '',
        quantityError: '',
      }));
      setRows(mapped);
    } else {
      setRows([{ id: '', itemSku: '', skuquantity: 0, itemSkuError: '', quantityError: '' }]);
    }
  }, [initialRows]);

  // Expose validation method to parent
  useImperativeHandle(ref, () => ({
    validateAndGetRows: () => {
      let isValid = true;
      const validated = rows.map(row => {
        const errors = { ...row };
        if (!row.itemSku.trim()) {
          errors.itemSkuError = 'Item SKU is required';
          isValid = false;
        }
        // Only enforce positive numeric quantity criteria if it's visible in edit mode
        if (isEditMode) {
          if (
            !String(row.skuquantity).trim() ||
            isNaN(row.skuquantity)
          ) {
            errors.quantityError = 'Quantity must be a positive number';
            isValid = false;
          }
        }
        return errors;
      });
      setRows(validated);
      return isValid ? validated.map(({ id, itemSku, skuquantity }) => ({ id, item_sku: itemSku, quantity: skuquantity })) : null;
    }
  }));

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    if (field === 'itemSku') updated[index].itemSkuError = '';
    if (field === 'skuquantity') updated[index].quantityError = '';
    setRows(updated);
  };

  const addRow = () => {
    if (rows.length < 10) {
      setRows([...rows, { id: '', itemSku: '', skuquantity: 0, itemSkuError: '', quantityError: '' }]);
    }
  };

  const removeRow = index => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
  };

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box', my: 1 }}>
      {/* Dynamic Subform Table Section Sticky Header Bar Wrapper */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#FFFFFF',
          zIndex: 5,
          py: 2,
          px: 1,
          borderBottom: '1px solid #E2E8F0',
          mb: 2.5
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 800, 
            color: '#0F172A', 
            fontSize: '15px', 
            letterSpacing: '-0.2px' 
          }}
        >
          Kit Assembly Items
        </Typography>
        
        <Button
          variant="outlined"
          onClick={addRow}
          disabled={rows.length >= 10}
          startIcon={<AddCircleOutline />}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: '13px',
            height: '34px',
            px: 2,
            borderColor: '#1976d2',
            color: '#1976d2',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              borderColor: '#115293',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              transform: 'scale(1.02)'
            },
            '&.Mui-disabled': {
              borderColor: '#E2E8F0',
              color: '#CBD5E1'
            }
          }}
        >
          Add Item
        </Button>
      </Box>

      {/* Repeated Components Fields Forms Dynamic Mapping Flow Box */}
      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center', 
            gap: 2,
            mb: 1.5,
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {/* Column A: Item Sku Autocomplete Field Box */}
          <Box sx={{ flex: 1, minHeight: '62px' }}>
            <Autocomplete
              options={skuOnly}
              value={row.itemSku}
              onChange={(e, newValue) => handleChange(index, 'itemSku', newValue || '')}
              onInputChange={(e, newInputValue) => handleChange(index, 'itemSku', newInputValue)}
              freeSolo
              sx={{
                ...enterpriseKitInputStyles,
                '& .MuiOutlinedInput-root': {
                  ...enterpriseKitInputStyles['& .MuiOutlinedInput-root'],
                  paddingTop: '0px !important',
                  paddingBottom: '0px !important',
                  minHeight: '39px !important', 
                  height: '39px !important',
                },
                '& .MuiAutocomplete-input': {
                  padding: '0px !important', 
                },
                '& .MuiAutocomplete-endAdornment': {
                  top: 'calc(50% - 14px)', 
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item Sku"
                  variant="outlined"
                  size="small"
                  error={!!row.itemSkuError}
                  helperText={row.itemSkuError}
                />
              )}
            />
          </Box>

          {/* Column B: Quantitative Sku Counter (Conditionally visible & Forced Read-only) */}
          {isEditMode && (
            <Box sx={{ flex: 1, minHeight: '62px' }}>
              <TextField
                label="Quantity"
                value={row.skuquantity}
                variant="filled" // Changes UI profile styles cleanly to standard enterprise read-only configs
                size="small"
                InputProps={{ readOnly: true }} // Locks downstream component modifications completely
                error={!!row.quantityError}
                helperText={row.quantityError}
                sx={enterpriseModalInputStyles}
              />
            </Box>
          )}

          {/* Column C: Subform entry deletion controls */}
          <Box sx={{ pb: '22px', display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => removeRow(index)}
              disabled={rows.length === 1}
              sx={{
                color: '#DC2626',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(220, 38, 38, 0.04)',
                  borderColor: '#FCA5A5',
                  color: '#B91C1C',
                  transform: 'scale(1.05)'
                },
                '&.Mui-disabled': {
                  borderColor: '#E2E8F0',
                  backgroundColor: '#F1F5F9',
                  color: '#CBD5E1'
                }
              }}
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
});

export default KitDetailsForm;